import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTrash,
    faEye,
    faSave,
    faSearch,
    faRefresh,
    faBoxes,
    faSpinner,
    faExclamationTriangle,
    faCheckCircle,
    faTimes,
    faChevronLeft,
    faChevronRight,
    faFilter,
    faCalendarAlt,
    faFilePdf,
    faPrint,
    faDownload,
    faCalendar,
    faTimesCircle,
    faBox,
    faTag,
    faBuilding,
    faBalanceScale,
    faDollarSign,
    faCalculator,
    faExclamationCircle,
    faHashtag,
    faPlus,
    faBarcode,
    faLayerGroup,
    faIndustry,
    faWeightHanging,
    faWarehouse,
    faFileExport,
    faChartLine,
    faPercent,
    faUser,
    faPalette,
    faRulerCombined,
    faBoxes as faBoxesIcon,
    faInfoCircle,
    faHistory,
    faStore,
    faExchangeAlt,
    faShoppingCart,
    faClock,
    faHourglassHalf,
    faTruck,
    faCheckDouble,
    faReceipt,
    faFileInvoice,
    faMoneyBill,
    faPhone,
    faEnvelope,
    faMapMarkerAlt,
    faIdCard,
    faCubes,
    faInfoCircle as faInfoCircleIcon,
    faRuler,
    faLayerGroup as faLayerGroupIcon,
    faEye as faEyeIcon
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import "../styles/CustomerList.css";
import "../styles/Customer.css";

// Create translator instance
const createFieldOrderListTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_fieldorder_list_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Field Order List");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_fieldorder_list_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        fieldOrderList: 'Fabric Orders List',
        allFieldOrders: 'My Fabric Orders',
        fieldOrders: 'Fabric Orders',

        shopName: 'Shop',
        shopCode: 'Shop Code',
        currentShop: 'Current Shop',
        noShopSelected: 'No shop selected. Please select a shop first.',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',

        searchPlaceholder: 'Search fabric orders...',
        refresh: 'Refresh',
        loadingFieldOrders: 'Loading fabric orders...',
        noFieldOrdersFound: 'No fabric orders found',

        orderDetails: 'Fabric Order Details',
        close: 'Close',

        serialNo: 'S.No',
        id: 'ID',
        orderNumber: 'Order #',
        orderDate: 'Order Date',
        customer: 'Customer',
        customerName: 'Customer Name',
        customerPhone: 'Phone',
        customerAddress: 'Address',
        customerGST: 'GST',
        totalAmount: 'Total Amount',
        status: 'Status',
        itemsCount: 'Items',
        totalYards: 'Total Yards',
        totalRolls: 'Total Rolls',
        actions: 'Actions',
        addedBy: 'Added By',
        shopInfo: 'Shop',

        view: 'View',
        edit: 'Edit',
        delete: 'Delete',

        deleteConfirm: 'Are you sure you want to delete this fabric order?',
        deletedSuccess: 'Fabric order deleted successfully',
        deleteError: 'Error deleting fabric order',
        networkError: 'Network error occurred',

        orderId: 'Order ID',
        customerInfo: 'Customer Information',
        productDetails: 'Product Details',
        productName: 'Product',
        colorCode: 'Color',
        category: 'Category',
        type: 'Type',
        brand: 'Brand',
        batchNo: 'Batch No',
        price: 'Price',
        quantity: 'Qty',
        total: 'Total',
        totalYardsLabel: 'Total Yards',
        totalRollsLabel: 'Total Rolls',
        unit: 'Unit',
        notProvided: 'Not provided',
        calculations: 'Calculations',
        subtotal: 'Subtotal',
        discountPercent: 'Discount %',
        discountAmount: 'Discount Amount',
        taxPercent: 'Tax %',
        taxAmount: 'Tax Amount',
        grandTotal: 'Grand Total',
        orderNotes: 'Order Notes',
        containerNo: 'Container No',
        routeDetails: 'Route Details',
        paymentInfo: 'Payment Information',
        paidAmount: 'Paid Amount',
        balance: 'Balance',
        paymentMethod: 'Payment Method',
        remarks: 'Remarks',

        statusOptions: {
            'pending': 'Pending',
            'processing': 'Processing',
            'approved': 'Approved',
            'delivered': 'Delivered',
            'completed': 'Completed',
            'cancelled': 'Cancelled'
        },

        showing: 'Showing',
        of: 'of',
        orders: 'orders',
        previous: 'Previous',
        next: 'Next',

        filter: 'Filter',
        all: 'All',
        filterBy: 'Filter By',
        selectFilter: 'Select Filter',
        orderNumberFilter: 'Order Number',
        customerFilter: 'Customer',
        statusFilter: 'Status',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        applyFilters: 'Apply Filters',
        resetFilters: 'Reset Filters',
        exportToPDF: 'Export PDF',
        printList: 'Print List',
        exportData: 'Export Data',
        filterResults: 'Filter Results',
        clearAll: 'Clear All',
        addedByFilter: 'Added By',

        editOrder: 'Edit Fabric Order',
        updateOrder: 'Update Order',
        updating: 'Updating...',
        orderUpdated: 'Fabric order updated successfully',
        updateError: 'Error updating fabric order',
        cancel: 'Cancel',
        saveChanges: 'Save Changes',
        fillDetails: 'Fill in the fabric order details below',

        customerRequired: 'Customer name is required',
        orderDateRequired: 'Order date is required',
        statusRequired: 'Status is required',
        itemsRequired: 'At least one item is required',
        invalidAmount: 'Please enter a valid amount',

        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this fabric order?',
        deleteWarning: 'This action cannot be undone.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',

        myRecords: 'My Records Only',
        loadingUserData: 'Loading your fabric order data...',
        noUserRecords: 'No fabric orders found in this shop',

        businessReport: 'Business Report',
        generatedBy: 'Generated by',
        fieldOrderReport: 'Fabric Order Report',
        orderSummary: 'Order Summary',

        success: 'Success',
        error: 'Error',
        warning: 'Warning',

        totalOrders: 'Total Orders',
        totalValue: 'Total Value',
        
        dateAdded: 'Date Added',
        lastUpdated: 'Last Updated',
        
        generatingPDF: 'Generating PDF...',
        pdfGenerationError: 'Error generating PDF',
        pdfGenerated: 'PDF generated successfully',
        reportFilters: 'Applied Filters',
        reportDate: 'Report Date',
        
        yards: 'Yards',
        meters: 'Meters',
        roll: 'Roll',
        bundle: 'Bundle',
        
        statusHistory: 'Status History',
        changedBy: 'Changed by',
        changedOn: 'Changed on',
        oldStatus: 'Old Status',
        newStatus: 'New Status',
        
        expectedDelivery: 'Expected Delivery',
        company: 'Company',
        bankInfo: 'Bank Information',
        bankName: 'Bank Name',
        accountNumber: 'Account Number'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const fieldOrderListTranslator = createFieldOrderListTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content shop-selector-modal">
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                        {shop.shop_role && (
                                            <div className="shop-item-role">Role: {shop.shop_role}</div>
                                        )}
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Order Items Table Component for View Modal
const OrderItemsTable = ({ items, currencySymbol = 'Rs', translations, showUnit = true }) => {
    if (!items || items.length === 0) {
        return (
            <div className="no-items-message">
                <p>No items found for this order</p>
            </div>
        );
    }

    const totalYards = items.reduce((sum, item) => sum + (parseFloat(item.total_yards) || 0), 0);
    const totalRolls = items.reduce((sum, item) => sum + (parseInt(item.total_rolls) || 0), 0);

    return (
        <div className="order-items-table-container">
            <table className="order-items-table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>{translations.productName || 'Product'}</th>
                        <th>{translations.colorCode || 'Color'}</th>
                        <th>{translations.batchNo || 'Batch No'}</th>
                        <th>{translations.type || 'Type'}</th>
                        <th>{translations.brand || 'Brand'}</th>
                        <th>{translations.unit || 'Unit'}</th>
                        <th>{translations.price || 'Price'}</th>
                        <th>{translations.quantity || 'Qty'}</th>
                        <th>{translations.total || 'Total'}</th>
                        <th>{translations.totalYardsLabel || 'Total Yards'}</th>
                    </tr>
                    </thead>
                <tbody>
                    {items.map((item, index) => (
                        <tr key={item.id || index}>
                            <td>{index + 1}</td>
                            <td>
                                {item.product_name}
                                {item.product_colorcode && (
                                    <span 
                                        className="color-badge" 
                                        style={{ backgroundColor: item.product_colorcode }}
                                        title={item.product_colorcode}
                                    ></span>
                                )}
                            </td>
                            <td>{item.product_colorcode || '-'}</td>
                            <td>{item.batch_no || '-'}</td>
                            <td>{item.type || '-'}</td>
                            <td>{item.brand || '-'}</td>
                            <td>{item.unit || 'meter'}</td>
                            <td>{currencySymbol} {parseFloat(item.price_per_unit || item.price || 0).toFixed(2)}</td>
                            <td>{parseFloat(item.quantity || 0).toFixed(3)}</td>
                            <td className="total-amount">{currencySymbol} {parseFloat(item.total || 0).toFixed(2)}</td>
                            <td>{parseFloat(item.total_yards || 0).toFixed(2)} yds</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan="9" className="text-right"><strong>{translations.totalAmount || 'Total Amount'}:</strong></td>
                        <td className="grand-total">
                            <strong>{currencySymbol} {items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0).toFixed(2)}</strong>
                        </td>
                        <td className="grand-total-yards">
                            <span className="total-yards-summary">
                                <FontAwesomeIcon icon={faRulerCombined} /> {totalYards.toFixed(2)} yds
                            </span>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

// Main Component
const FieldOrderList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [orderToDelete, setOrderToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);

    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    const toastTimerRef = useRef(null);

    const [editFormData, setEditFormData] = useState({
        order_date: '',
        order_number: '',
        customer_name: '',
        status: '',
        notes: '',
        discount_percent: '0',
        tax_percent: '0'
    });
    const [editFormErrors, setEditFormErrors] = useState({});

    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        businessaddress: '',
        fullname: '',
        email: ''
    });

    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#8b5cf6';
    });

    const [translations, setTranslations] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    const [filterBy, setFilterBy] = useState('all');
    const [orderNumberFilter, setOrderNumberFilter] = useState('');
    const [customerFilter, setCustomerFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'pending', label: 'Pending' },
        { value: 'processing', label: 'Processing' },
        { value: 'approved', label: 'Approved' },
        { value: 'delivered', label: 'Delivered' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    const totalOrders = filteredOrders.length;
    const totalAmount = filteredOrders.reduce((sum, order) => sum + (parseFloat(order.grand_total) || 0), 0);

    const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentOrders = filteredOrders.slice(startIndex, endIndex);

    const showToast = (type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    };

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'customer-primary-color': color,
                    'customer-primary-dark': adjust(-40),
                    'customer-primary-light': adjust(40),
                    'customer-primary-ultralight': adjust(20),
                    'customer-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'customer-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });

                document.documentElement.style.setProperty('--primary-color', color);
                document.documentElement.style.setProperty('--primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--primary-light', adjust(40));
                document.documentElement.style.setProperty('--primary-ultralight', adjust(20));
                document.documentElement.style.setProperty('--primary-gradient', `linear-gradient(135deg, ${color}, ${adjust(-20)})`);
                document.documentElement.style.setProperty('--primary-shadow', `${color}20`);
                document.documentElement.style.setProperty('--row-active', adjust(10));

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(currentTheme);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
            if (theme !== currentTheme) {
                setCurrentTheme(theme);
                applyTheme(theme);
            }
        };

        handleThemeChange();

        window.addEventListener('storage', handleThemeChange);

        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [currentTheme]);

    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await fieldOrderListTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
            }
        };

        handleLanguageChange();

        window.addEventListener('storage', handleLanguageChange);

        const interval = setInterval(handleLanguageChange, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
            document.documentElement.style.direction = 'rtl';
            document.documentElement.lang = currentLanguage;
            const tables = document.querySelectorAll('.customer-list-modern, .table-container-modern, .customer-table-modern');
            tables.forEach(table => {
                table.style.direction = 'rtl';
                table.style.textAlign = 'right';
            });
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
            document.documentElement.lang = currentLanguage;
            const tables = document.querySelectorAll('.customer-list-modern, .table-container-modern, .customer-table-modern');
            tables.forEach(table => {
                table.style.direction = 'ltr';
                table.style.textAlign = 'left';
            });
        }

        return () => {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
        };
    }, [currentLanguage]);

    useEffect(() => {
        if (user && user.username) {
            fetchFieldOrders();
            fetchBusinessInfo();
            fetchAvailableShops();
        } else {
            setLoading(false);
            showToast('error', 'User not authenticated. Please login again.');
        }
    }, [user, currentShop]);

    useEffect(() => {
        applyFilters();
    }, [orders, searchTerm, filterBy, orderNumberFilter, customerFilter, statusFilter, addedByFilter, startDate, endDate]);

    const fetchAvailableShops = async () => {
        if (!user) return;

        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            setAvailableShops([]);
        } finally {
            setLoadingShops(false);
        }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(
                shop.shop_id,
                shop.shop_name,
                shop.shop_code
            );

            if (success) {
                showToast('success', 'Shop switched successfully');
                fetchFieldOrders();
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    };

    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button
                        className="btn-modern btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faStore} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }
        return null;
    };

    const fetchBusinessInfo = async () => {
        if (!user || !user.username) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/field_orders.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo({
                    businessname: data.data?.businessname || '',
                    bussinesslogo: data.data?.bussinesslogo || '',
                    businessaddress: data.data?.businessaddress || '',
                    fullname: data.data?.fullname || '',
                    email: data.data?.email || ''
                });
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
        }
    };

    const fetchFieldOrders = async () => {
        if (!user || !user.username) {
            showToast('error', 'User information not available. Please login again.');
            setLoading(false);
            return;
        }

        if (!currentShop) {
            showToast('error', translations.noShopSelected || 'Please select a shop first');
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/field_orders.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setOrders(data.data || []);
                resetFilters();

                if (data.data.length === 0) {
                    showToast('info', translations.noUserRecords || 'No fabric orders found in this shop');
                }
            } else {
                showToast('error', data.message || translations.networkError || 'Network error occurred');
            }
        } catch (error) {
            console.error('Error fetching fabric orders:', error);
            showToast('error', translations.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...orders];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(order =>
                order.order_number?.toLowerCase().includes(term) ||
                order.customer_name?.toLowerCase().includes(term) ||
                order.added_by_username?.toLowerCase().includes(term) ||
                order.username?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'orderNumber' && orderNumberFilter.trim()) {
            const term = orderNumberFilter.toLowerCase();
            filtered = filtered.filter(order =>
                order.order_number?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'customer' && customerFilter.trim()) {
            const term = customerFilter.toLowerCase();
            filtered = filtered.filter(order =>
                order.customer_name?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'status' && statusFilter) {
            filtered = filtered.filter(order =>
                order.status === statusFilter
            );
        }

        if (filterBy === 'added_by' && addedByFilter.trim()) {
            const term = addedByFilter.toLowerCase();
            filtered = filtered.filter(order =>
                (order.added_by_username?.toLowerCase().includes(term)) ||
                (order.username?.toLowerCase().includes(term))
            );
        }

        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(order => {
                if (!order.order_date) return false;
                const orderDate = new Date(order.order_date);
                orderDate.setHours(0, 0, 0, 0);
                return orderDate >= start;
            });
        }

        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(order => {
                if (!order.order_date) return false;
                const orderDate = new Date(order.order_date);
                return orderDate <= end;
            });
        }

        setFilteredOrders(filtered);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilterBy('all');
        setOrderNumberFilter('');
        setCustomerFilter('');
        setStatusFilter('');
        setAddedByFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
    };

    const handleSearch = () => {
        applyFilters();
    };

    const exportToPDF = async () => {
        if (!filteredOrders || filteredOrders.length === 0) {
            showToast('warning', 'No data to export');
            return;
        }

        setPdfLoading(true);
        showToast('info', translations.generatingPDF || 'Generating PDF...');

        try {
            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });

            if (businessInfo.bussinesslogo) {
                try {
                    doc.addImage(businessInfo.bussinesslogo, 'PNG', 10, 5, 30, 15);
                } catch (e) {
                    console.log('Could not add logo:', e);
                }
            }

            doc.setFontSize(16);
            doc.setTextColor(0, 0, 0);
            doc.text(translations.fieldOrderReport || 'Fabric Order Report', 148, 15, { align: 'center' });

            doc.setFontSize(10);
            doc.text(businessInfo.businessname || user?.business_name || '', 148, 22, { align: 'center' });
            
            if (businessInfo.businessaddress) {
                doc.setFontSize(8);
                doc.text(businessInfo.businessaddress, 148, 27, { align: 'center' });
            }

            doc.setFontSize(8);
            const reportDate = new Date().toLocaleDateString();
            const generatedBy = `${translations.generatedBy || 'Generated by'}: ${user?.fullname || user?.username}`;
            doc.text(`${translations.reportDate || 'Report Date'}: ${reportDate} | ${generatedBy}`, 148, 32, { align: 'center' });

            if (currentShop) {
                doc.text(`${translations.shopName || 'Shop'}: ${currentShop.shop_name} (${currentShop.shop_code || ''})`, 148, 37, { align: 'center' });
            }

            let yPos = 42;
            const filterLines = [];
            
            if (filterBy !== 'all') {
                let filterText = `${translations.filterBy || 'Filter By'}: `;
                switch (filterBy) {
                    case 'orderNumber':
                        filterText += `${translations.orderNumberFilter || 'Order Number'} = ${orderNumberFilter}`;
                        break;
                    case 'customer':
                        filterText += `${translations.customerFilter || 'Customer'} = ${customerFilter}`;
                        break;
                    case 'status':
                        filterText += `${translations.statusFilter || 'Status'} = ${statusFilter}`;
                        break;
                    case 'added_by':
                        filterText += `${translations.addedByFilter || 'Added By'} = ${addedByFilter}`;
                        break;
                    default:
                        filterText = '';
                }
                if (filterText) filterLines.push(filterText);
            }
            
            if (startDate && endDate) {
                filterLines.push(`${translations.dateRange || 'Date Range'}: ${startDate} to ${endDate}`);
            } else if (startDate) {
                filterLines.push(`${translations.startDate || 'Start Date'}: ${startDate}`);
            } else if (endDate) {
                filterLines.push(`${translations.endDate || 'End Date'}: ${endDate}`);
            }

            if (filterLines.length > 0) {
                doc.setFontSize(8);
                doc.setTextColor(100, 100, 100);
                filterLines.forEach((line, index) => {
                    doc.text(line, 10, yPos + (index * 4));
                });
                yPos += filterLines.length * 4 + 2;
            } else {
                yPos += 2;
            }

            doc.setFontSize(9);
            doc.setTextColor(0, 0, 0);
            doc.text(`${translations.totalOrders || 'Total Orders'}: ${totalOrders}`, 10, yPos);
            doc.text(`${translations.totalValue || 'Total Value'}: Rs ${totalAmount.toFixed(2)}`, 10, yPos + 5);

            const tableData = filteredOrders.map(order => [
                order.order_number || '-',
                formatDate(order.order_date),
                order.customer_name || '-',
                `Rs ${parseFloat(order.grand_total || 0).toFixed(2)}`,
                translations.statusOptions?.[order.status] || order.status || '-',
                order.added_by_username || order.username || '-'
            ]);

            autoTable(doc, {
                startY: yPos + 10,
                head: [[
                    translations.orderNumber || 'Order #',
                    translations.orderDate || 'Date',
                    translations.customerName || 'Customer',
                    translations.totalAmount || 'Total',
                    translations.status || 'Status',
                    translations.addedBy || 'Added By'
                ]],
                body: tableData,
                theme: 'striped',
                headStyles: {
                    fillColor: [139, 92, 246],
                    textColor: [255, 255, 255],
                    fontSize: 8,
                    halign: 'center'
                },
                bodyStyles: {
                    fontSize: 7
                },
                margin: { top: yPos + 10, left: 5, right: 5 },
                didDrawPage: (data) => {
                    doc.setFontSize(7);
                    doc.setTextColor(150, 150, 150);
                    doc.text(
                        `${translations.generatedBy || 'Generated by'}: OurZest POS - Page ${doc.getCurrentPageInfo().pageNumber}`,
                        data.settings.margin.left,
                        doc.internal.pageSize.height - 5
                    );
                }
            });

            const filename = `fabric_orders_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(filename);

            showToast('success', translations.pdfGenerated || 'PDF generated successfully');
        } catch (error) {
            console.error('Error generating PDF:', error);
            showToast('error', translations.pdfGenerationError || 'Error generating PDF');
        } finally {
            setPdfLoading(false);
        }
    };

    const handleView = async (order) => {
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/field_orders.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_record',
                    id: order.id,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                const fullOrder = {
                    ...data.data.order,
                    items: data.data.items || []
                };
                setSelectedOrder(fullOrder);
                setShowViewModal(true);
            } else {
                showToast('error', 'Failed to load order details');
            }
        } catch (error) {
            console.error('Error loading order details:', error);
            showToast('error', 'Failed to load order details');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (order) => {
        if (order.username !== user.username && order.added_by_username !== user.username) {
            showToast('error', 'You can only edit your own fabric orders');
            return;
        }

        setSelectedOrder(order);
        setEditFormData({
            order_date: order.order_date || '',
            order_number: order.order_number || '',
            customer_name: order.customer_name || '',
            status: order.status || 'pending',
            notes: order.notes || '',
            discount_percent: order.discount_percent || '0',
            tax_percent: order.tax_percent || '0'
        });
        setEditFormErrors({});
        setShowEditModal(true);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        if (!currentShop) {
            showToast('error', translations.noShopSelected || 'No shop selected');
            return;
        }

        setEditLoading(true);   

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/field_orders.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'update_status',
                    id: selectedOrder.id,
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    status: editFormData.status
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.orderUpdated || 'Fabric order updated successfully');
                setShowEditModal(false);
                fetchFieldOrders();
            } else {
                showToast('error', data.message || translations.updateError || 'Error updating fabric order');
            }
        } catch (error) {
            console.error('Error updating fabric order:', error);
            showToast('error', translations.updateError || 'Error updating fabric order');
        } finally {
            setEditLoading(false);
        }
    };

    const handleDeleteClick = (orderId) => {
        const order = orders.find(o => o.id === orderId);
        if (!order || (order.username !== user.username && order.added_by_username !== user.username)) {
            showToast('error', 'You can only delete your own fabric orders');
            return;
        }

        setOrderToDelete(orderId);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!orderToDelete || !currentShop) return;

        const order = orders.find(o => o.id === orderToDelete);
        if (!order || (order.username !== user.username && order.added_by_username !== user.username)) {
            showToast('error', 'You can only delete your own fabric orders');
            setShowDeleteModal(false);
            setOrderToDelete(null);
            return;
        }

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/field_orders.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'delete',
                    id: orderToDelete,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.deletedSuccess || 'Fabric order deleted successfully');
                fetchFieldOrders();
                setShowDeleteModal(false);
                setOrderToDelete(null);
            } else {
                showToast('error', data.message || translations.deleteError || 'Error deleting fabric order');
            }
        } catch (error) {
            console.error('Error deleting fabric order:', error);
            showToast('error', translations.deleteError || 'Error deleting fabric order');
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            return dateString;
        }
    };

    const formatNumber = (num, decimals = 2) => {
        if (num === undefined || num === null || num === '') return '';
        const value = parseFloat(num).toFixed(decimals);
        return value.replace(/\.?0+$/, '');
    };

    const getStatusBadge = (status) => {
        const statusColors = {
            'pending': { bg: '#f59e0b20', color: '#f59e0b', border: '#f59e0b40' },
            'processing': { bg: '#3b82f620', color: '#3b82f6', border: '#3b82f640' },
            'approved': { bg: '#10b98120', color: '#10b981', border: '#10b98140' },
            'delivered': { bg: '#8b5cf620', color: '#8b5cf6', border: '#8b5cf640' },
            'completed': { bg: '#05966920', color: '#059669', border: '#05966940' },
            'cancelled': { bg: '#ef444420', color: '#ef4444', border: '#ef444440' }
        };
        return statusColors[status] || { bg: '#6b728020', color: '#6b7280', border: '#6b728040' };
    };

    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    useEffect(() => {
        if (startDate && endDateRef.current) {
            endDateRef.current.min = startDate;
        }
    }, [startDate]);

    useEffect(() => {
        const today = getTodayDate();
        if (startDateRef.current) {
            startDateRef.current.max = today;
        }
        if (endDateRef.current) {
            endDateRef.current.max = today;
        }
    }, []);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`customer-list-modern ${isRTL ? 'rtl' : ''}`}>
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faShoppingCart} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.fieldOrderList || 'Fabric Orders List'}</h1>
                        <div className="shop-info">
                            <p>
                                {currentShop?.shop_name
                                    ? `${translations.shopName || 'Shop'}: ${currentShop.shop_name}`
                                    : `${translations.allFieldOrders || 'My Fabric Orders'} - ${user?.username}`}
                            </p>
                        </div>
                    </div>
                </div>

                {renderShopIndicator()}
            </div>

            <div className="advanced-filters-section">
                <div className="filters-row">
                    <div className="filter-group compact">
                        <select
                            value={filterBy}
                            onChange={(e) => setFilterBy(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.all || 'All'}</option>
                            <option value="orderNumber">{translations.orderNumberFilter || 'Order Number'}</option>
                            <option value="customer">{translations.customerFilter || 'Customer'}</option>
                            <option value="status">{translations.statusFilter || 'Status'}</option>
                            <option value="date">{translations.dateRange || 'Date Range'}</option>
                            <option value="added_by">{translations.addedByFilter || 'Added By'}</option>
                        </select>
                    </div>

                    {filterBy === 'orderNumber' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={orderNumberFilter}
                                onChange={(e) => setOrderNumberFilter(e.target.value)}
                                placeholder={translations.orderNumberFilter || 'Order number...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'customer' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={customerFilter}
                                onChange={(e) => setCustomerFilter(e.target.value)}
                                placeholder={translations.customerFilter || 'Customer name...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'status' && (
                        <div className="filter-group compact">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="filter-select"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            >
                                {statusOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {filterBy === 'added_by' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={addedByFilter}
                                onChange={(e) => setAddedByFilter(e.target.value)}
                                placeholder={translations.addedByFilter || 'Added by username...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'date' && (
                        <>
                            <div className="filter-group compact date-filter">
                                <input
                                    ref={startDateRef}
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    max={getTodayDate()}
                                    className="filter-input compact date-input"
                                    title={translations.startDate || 'Start Date'}
                                />
                            </div>
                            <div className="filter-group compact date-filter">
                                <input
                                    ref={endDateRef}
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={startDate}
                                    max={getTodayDate()}
                                    className="filter-input compact date-input"
                                    title={translations.endDate || 'End Date'}
                                />
                            </div>
                        </>
                    )}

                    <div className="filter-group compact search-group">
                        <div className="search-box compact">
                            <input
                                type="text"
                                placeholder={translations.searchPlaceholder || 'Search fabric orders...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    </div>

                    <div className="filter-group compact">
                        <button
                            onClick={fetchFieldOrders}
                            className="refresh-btn-modern compact"
                            disabled={loading}
                            title={translations.refresh || 'Refresh'}
                        >
                            <FontAwesomeIcon icon={faRefresh} />
                            <span className="btn-text">{translations.refresh || 'Refresh'}</span>
                        </button>
                    </div>

                    <div className="filter-group compact">
                        <button
                            onClick={exportToPDF}
                            className="refresh-btn-modern compact"
                            disabled={pdfLoading || filteredOrders.length === 0}
                            title={translations.exportToPDF || 'Export to PDF'}
                        >
                            <FontAwesomeIcon icon={faFilePdf} spin={pdfLoading} />
                            <span className="btn-text">
                                {pdfLoading ? (translations.generatingPDF || 'Generating...') : (translations.exportToPDF || 'PDF')}
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner-modern"></div>
                    <p>{translations.loadingUserData || 'Loading your fabric order data...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="no-shop-container">
                    <div className="no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShop || 'Please select a shop to view fabric orders'}</p>
                        <button
                            className="btn-modern btn-primary-modern"
                            onClick={() => setShowShopSelector(true)}
                        >
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="table-scroll-container">
                        <div className="table-container-modern">
                            <div className="customer-table-wrapper">
                                <table className="customer-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="id-column centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="centered-cell">{translations.orderNumber || 'Order #'}</th>
                                            <th className="centered-cell">{translations.orderDate || 'Date'}</th>
                                            <th className="centered-cell">{translations.shopName || 'Shop'}</th>
                                            <th className="centered-cell">{translations.customerName || 'Customer'}</th>
                                            <th className="centered-cell">{translations.totalAmount || 'Total'}</th>
                                            <th className="centered-cell">{translations.status || 'Status'}</th>
                                            <th className="centered-cell">{translations.itemsCount || 'Items'}</th>
                                            <th className="centered-cell">{translations.totalYards || 'Total Yards'}</th>
                                            <th className="centered-cell">{translations.totalRolls || 'Total Rolls'}</th>
                                            <th className="centered-cell">{translations.addedBy || 'Added By'}</th>
                                            <th className="actions-column centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentOrders.map((order, index) => {
                                            const addedBy = order.added_by_username || order.username || '-';
                                            const isSelfAdded = addedBy === user?.username;
                                            const statusStyle = getStatusBadge(order.status);
                                            // Calculate totals from items if available, otherwise use order totals
                                            const totalItems = order.total_items || 0;
                                            const totalYards = order.total_yards || 0;
                                            const totalRolls = order.total_rolls || 0;

                                            return (
                                                <tr key={order.id}>
                                                    <td className="id-cell centered-cell">
                                                        <span className="cell-text">{startIndex + index + 1}</span>
                                                     </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text order-number">{order.order_number || '-'}</span>
                                                     </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text">{formatDate(order.order_date)}</span>
                                                     </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text shop-name">{order.shop_name || '-'}</span>
                                                     </td>
                                                    <td className="centered-cell">
                                                        <div className="customer-details">
                                                            <span className="customer-name">{order.customer_name}</span>
                                                        </div>
                                                     </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text total-amount">
                                                            Rs {formatNumber(order.grand_total)}
                                                        </span>
                                                     </td>
                                                    <td className="centered-cell">
                                                        <div
                                                            className="status-badge"
                                                            style={{
                                                                backgroundColor: statusStyle.bg,
                                                                color: statusStyle.color,
                                                                border: `1px solid ${statusStyle.border}`
                                                            }}
                                                        >
                                                            {translations.statusOptions?.[order.status] || order.status || '-'}
                                                        </div>
                                                     </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text items-count">{totalItems}</span>
                                                     </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text total-yards-order">
                                                            {parseFloat(totalYards).toFixed(2)} yds
                                                        </span>
                                                     </td>
                                                    <td className="centered-cell">
                                                        <span className="cell-text total-rolls-order">
                                                            {totalRolls}
                                                        </span>
                                                     </td>
                                                    <td className="centered-cell">
                                                        <span className={`added-by-badge ${isSelfAdded ? 'self' : ''}`}>
                                                            <span className="cell-text">{addedBy}</span>
                                                        </span>
                                                     </td>
                                                    <td className="actions-cell-modern centered-cell">
                                                        <div className="actions-content">
                                                            <button
                                                                onClick={() => handleView(order)}
                                                                className="action-btn-modern view-btn-modern"
                                                                title={translations.view || 'View'}
                                                            >
                                                                <FontAwesomeIcon icon={faEyeIcon} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleEdit(order)}
                                                                className="action-btn-modern edit-btn-modern"
                                                                title={translations.edit || 'Edit'}
                                                                disabled={!isSelfAdded}
                                                            >
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteClick(order.id)}
                                                                className="action-btn-modern delete-btn-modern"
                                                                title={translations.delete || 'Delete'}
                                                                disabled={!isSelfAdded}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                     </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {currentOrders.length === 0 && !loading && currentShop && (
                                    <div className="no-data-modern">
                                        <FontAwesomeIcon icon={faShoppingCart} />
                                        <p>{user ? (translations.noUserRecords || 'No fabric orders found in this shop') : (translations.noFieldOrdersFound || 'No fabric orders found')}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {filteredOrders.length > 0 && currentShop && (
                        <div className="pagination-modern">
                            <div className="pagination-info">
                                <span>{translations.showing || 'Showing'} {startIndex + 1}-{Math.min(endIndex, filteredOrders.length)} {translations.of || 'of'} {filteredOrders.length} {translations.orders || 'orders'}</span>
                                {currentShop?.shop_name && (
                                    <span className="shop-info-pagination">
                                        | {translations.shopName || 'Shop'}: {currentShop.shop_name}
                                    </span>
                                )}
                            </div>
                            <div className="pagination-controls">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{translations.previous || 'Previous'}</span>
                                </button>

                                <div className="page-numbers">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    <span>{translations.next || 'Next'}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal */}
            {showViewModal && selectedOrder && (
                <div className="modal-overlay-modern" onClick={() => setShowViewModal(false)}>
                    <div className="modal-content-modern view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faShoppingCart} />
                            </div>
                            <div className="modal-title">
                                <h3>{translations.orderDetails || 'Fabric Order Details'}</h3>
                                <span className="order-number-badge">#{selectedOrder.order_number}</span>
                            </div>
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowViewModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern view-modal-body">
                            {/* Order Summary */}
                            <div className="view-section">
                                <h4 className="view-section-title">
                                    <FontAwesomeIcon icon={faFileInvoice} />
                                    Order Summary
                                </h4>
                                <div className="view-grid">
                                    <div className="view-row">
                                        <span className="view-label">{translations.orderId || 'Order ID'}:</span>
                                        <span className="view-value">#{selectedOrder.id}</span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">{translations.orderNumber || 'Order Number'}:</span>
                                        <span className="view-value">{selectedOrder.order_number || '-'}</span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">{translations.orderDate || 'Order Date'}:</span>
                                        <span className="view-value">{formatDate(selectedOrder.order_date)}</span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">{translations.shopName || 'Shop'}:</span>
                                        <span className="view-value">{selectedOrder.shop_name || '-'}</span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">{translations.status || 'Status'}:</span>
                                        <span className="view-value">
                                            <div className="status-badge" style={getStatusBadge(selectedOrder.status)}>
                                                {translations.statusOptions?.[selectedOrder.status] || selectedOrder.status}
                                            </div>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Customer Information */}
                            <div className="view-section">
                                <h4 className="view-section-title">
                                    <FontAwesomeIcon icon={faUser} />
                                    {translations.customerInfo || 'Customer Information'}
                                </h4>
                                <div className="view-grid">
                                    <div className="view-row">
                                        <span className="view-label">{translations.customerName || 'Customer Name'}:</span>
                                        <span className="view-value">{selectedOrder.customer_name || '-'}</span>
                                    </div>
                                    {selectedOrder.customer_phone && (
                                        <div className="view-row">
                                            <span className="view-label">{translations.customerPhone || 'Phone'}:</span>
                                            <span className="view-value">{selectedOrder.customer_phone}</span>
                                        </div>
                                    )}
                                    {selectedOrder.customer_address && (
                                        <div className="view-row">
                                            <span className="view-label">{translations.customerAddress || 'Address'}:</span>
                                            <span className="view-value">{selectedOrder.customer_address}</span>
                                        </div>
                                    )}
                                    {selectedOrder.customer_gst && (
                                        <div className="view-row">
                                            <span className="view-label">{translations.customerGST || 'GST'}:</span>
                                            <span className="view-value">{selectedOrder.customer_gst}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Order Items */}
                            <div className="view-section">
                                <h4 className="view-section-title">
                                    <FontAwesomeIcon icon={faBoxes} />
                                    {translations.productDetails || 'Order Items'}
                                </h4>
                                <OrderItemsTable 
                                    items={selectedOrder.items || []} 
                                    currencySymbol="Rs"
                                    translations={translations}
                                    showUnit={true}
                                />
                            </div>

                            {/* Calculations */}
                            <div className="view-section">
                                <h4 className="view-section-title">
                                    <FontAwesomeIcon icon={faCalculator} />
                                    {translations.calculations || 'Calculations'}
                                </h4>
                                <div className="view-grid calculations-grid">
                                    <div className="view-row">
                                        <span className="view-label">{translations.subtotal || 'Subtotal'}:</span>
                                        <span className="view-value">Rs {formatNumber(selectedOrder.subtotal)}</span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">{translations.discountPercent || 'Discount %'}:</span>
                                        <span className="view-value">{selectedOrder.discount_percent || 0}%</span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">{translations.discountAmount || 'Discount Amount'}:</span>
                                        <span className="view-value text-negative">- Rs {formatNumber(selectedOrder.discount_amount)}</span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">{translations.taxPercent || 'Tax %'}:</span>
                                        <span className="view-value">{selectedOrder.tax_percent || 0}%</span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">{translations.taxAmount || 'Tax Amount'}:</span>
                                        <span className="view-value text-positive">+ Rs {formatNumber(selectedOrder.tax_amount)}</span>
                                    </div>
                                    <div className="view-row grand-total-row">
                                        <span className="view-label"><strong>{translations.grandTotal || 'Grand Total'}:</strong></span>
                                        <span className="view-value grand-total-value">Rs {formatNumber(selectedOrder.grand_total)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Order Notes */}
                            {selectedOrder.notes && (
                                <div className="view-section">
                                    <h4 className="view-section-title">
                                        <FontAwesomeIcon icon={faReceipt} />
                                        {translations.orderNotes || 'Order Notes'}
                                    </h4>
                                    <div className="remarks-box">
                                        {selectedOrder.notes}
                                    </div>
                                </div>
                            )}

                            {/* Metadata */}
                            <div className="view-section metadata-section">
                                <div className="view-grid">
                                    <div className="view-row">
                                        <span className="view-label">{translations.addedBy || 'Added By'}:</span>
                                        <span className="view-value">
                                            <span className="added-by-badge">
                                                {selectedOrder.added_by_username || selectedOrder.username || '-'}
                                            </span>
                                        </span>
                                    </div>
                                    <div className="view-row">
                                        <span className="view-label">{translations.dateAdded || 'Date Added'}:</span>
                                        <span className="view-value">{formatDate(selectedOrder.created_at) || formatDate(selectedOrder.order_date)}</span>
                                    </div>
                                    {selectedOrder.updated_at && selectedOrder.updated_at !== selectedOrder.created_at && (
                                        <div className="view-row">
                                            <span className="view-label">{translations.lastUpdated || 'Last Updated'}:</span>
                                            <span className="view-value">{formatDate(selectedOrder.updated_at)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button
                                className="btn-modern btn-secondary-modern"
                                onClick={() => setShowViewModal(false)}
                            >
                                {translations.close || 'Close'}
                            </button>
                            {(selectedOrder.username === user?.username || selectedOrder.added_by_username === user?.username) && (
                                <button
                                    className="btn-modern btn-primary-modern"
                                    onClick={() => {
                                        setShowViewModal(false);
                                        handleEdit(selectedOrder);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faEdit} />
                                    {translations.edit || 'Edit'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedOrder && (
                <div className="modal-overlay-modern" onClick={() => setShowEditModal(false)}>
                    <div className="modal-content-modern edit-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faEdit} />
                            </div>
                            <div className="modal-title">
                                <h3>{translations.editOrder || 'Edit Fabric Order'}</h3>
                                <span className="order-number-badge">#{selectedOrder.order_number}</span>
                            </div>
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowEditModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleEditSubmit}>
                            <div className="modal-body-modern">
                                <div className="edit-form-grid">
                                    <div className="form-group-modern">
                                        <label>{translations.customerName || 'Customer Name'}</label>
                                        <input
                                            type="text"
                                            value={editFormData.customer_name}
                                            onChange={(e) => setEditFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                                            className="form-input"
                                            required
                                            disabled
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>

                                    <div className="form-group-modern">
                                        <label>{translations.orderNumber || 'Order Number'}</label>
                                        <input
                                            type="text"
                                            value={editFormData.order_number}
                                            className="form-input"
                                            disabled
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>

                                    <div className="form-group-modern">
                                        <label>{translations.orderDate || 'Order Date'}</label>
                                        <input
                                            type="date"
                                            value={editFormData.order_date}
                                            className="form-input"
                                            disabled
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>

                                    <div className="form-group-modern">
                                        <label>{translations.status || 'Status'}</label>
                                        <select
                                            value={editFormData.status}
                                            onChange={(e) => setEditFormData(prev => ({ ...prev, status: e.target.value }))}
                                            className="form-input"
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        >
                                            <option value="pending">Pending</option>
                                            <option value="processing">Processing</option>
                                            <option value="approved">Approved</option>
                                            <option value="delivered">Delivered</option>
                                            <option value="completed">Completed</option>
                                            <option value="cancelled">Cancelled</option>
                                        </select>
                                    </div>

                                    <div className="form-group-modern full-width">
                                        <label>{translations.orderNotes || 'Order Notes'}</label>
                                        <textarea
                                            value={editFormData.notes}
                                            onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))}
                                            className="form-input"
                                            rows="3"
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer-modern">
                                <button
                                    type="button"
                                    className="btn-modern btn-secondary-modern"
                                    onClick={() => setShowEditModal(false)}
                                >
                                    {translations.cancel || 'Cancel'}
                                </button>
                                <button
                                    type="submit"
                                    className="btn-modern btn-primary-modern"
                                    disabled={editLoading}
                                >
                                    {editLoading ? (
                                        <>
                                            <div className="btn-spinner"></div>
                                            <span>{translations.updating || 'Updating...'}</span>
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSave} />
                                            <span>{translations.saveChanges || 'Save Changes'}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content-modern delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern delete-header">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </div>
                            <div className="modal-title">
                                <h3 style={{ color: 'var(--customer-error-color)' }}>
                                    {translations.confirmDelete || 'Confirm Delete'}
                                </h3>
                            </div>
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="delete-message">
                                <p style={{ fontSize: '1.1rem', marginBottom: '10px', color: 'var(--customer-text-primary)' }}>
                                    {translations.deleteMessage || 'Are you sure you want to delete this fabric order?'}
                                </p>
                                <p style={{ fontSize: '0.9rem', color: 'var(--customer-warning-color)' }}>
                                    <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                                    {translations.deleteWarning || 'This action cannot be undone.'}
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button
                                type="button"
                                className="btn-modern btn-secondary-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                {translations.noCancel || 'No, Cancel'}
                            </button>
                            <button
                                type="button"
                                className="btn-modern btn-delete-modern"
                                onClick={handleDelete}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                                {translations.yesDelete || 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FieldOrderList;